// Design history for a pipeline: the revision list (…/pipelines/{p}/revisions,
// cut by the revision worker, runs, restores, pins), a diff between any
// revision and the current design, Roll back (= a new revision whose `after`
// is the old design), plus Clone and copyable ids.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCollection } from '../../data/firestore';
import { pipelineApi, copyText, shortSha } from './api';

const when = (t) => (t ? new Date(t).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const mono = { fontFamily: 'ui-monospace, monospace', fontSize: 11.5 };

// Field-level diff for the History drawer: block adds/removes/edits by id,
// then every other changed key as before → after JSON.
export function designDiff(before, after) {
  const out = [];
  const a = Object.fromEntries(((before?.graph || {}).blocks || []).map((b) => [b.id, b]));
  const b = Object.fromEntries(((after?.graph || {}).blocks || []).map((x) => [x.id, x]));
  for (const id of new Set([...Object.keys(a), ...Object.keys(b)])) {
    if (!a[id]) out.push({ kind: 'block', id, change: 'added', text: `${b[id].label || b[id].type} (${b[id].type})` });
    else if (!b[id]) out.push({ kind: 'block', id, change: 'removed', text: `${a[id].label || a[id].type} (${a[id].type})` });
    else {
      const { position: _pa, ...ra } = a[id]; const { position: _pb, ...rb } = b[id]; // eslint-disable-line no-unused-vars
      if (JSON.stringify(ra) !== JSON.stringify(rb)) {
        const keys = [...new Set([...Object.keys(ra.config || {}), ...Object.keys(rb.config || {})])].filter((k) => JSON.stringify(ra.config?.[k]) !== JSON.stringify(rb.config?.[k]));
        const other = ['label', 'compute', 'classification', 'retry'].filter((k) => JSON.stringify(ra[k]) !== JSON.stringify(rb[k]));
        out.push({ kind: 'block', id, change: 'edited', text: `${rb.label || rb.type}: ${[...other, ...keys.map((k) => `config.${k}`)].join(', ') || 'changed'}`, before: ra, after: rb });
      }
    }
  }
  const ca = (before?.graph?.connections || []).map((c) => c.id).sort().join(','); const cb = (after?.graph?.connections || []).map((c) => c.id).sort().join(',');
  if (ca !== cb) out.push({ kind: 'wires', change: 'edited', text: `${(after?.graph?.connections || []).length} connection(s)` });
  for (const k of new Set([...Object.keys(before || {}), ...Object.keys(after || {})])) {
    if (k === 'graph') continue;
    if (JSON.stringify(before?.[k]) !== JSON.stringify(after?.[k])) out.push({ kind: 'field', id: k, change: before?.[k] === undefined ? 'added' : after?.[k] === undefined ? 'removed' : 'edited', text: k, before: before?.[k], after: after?.[k] });
  }
  return out;
}

export default function HistoryPanel({ orgId, workspaceId, pipelineId, pipeline, onRestored, onError }) {
  const path = `orgs/${orgId}/workspaces/${workspaceId}/pipelines/${pipelineId}/revisions`;
  const { docs } = useCollection(path, { orderBy: 'version', dir: 'desc' });
  const api = pipelineApi(orgId, workspaceId, pipelineId);
  const [open, setOpen] = useState(null); // revision id whose diff is open
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [cloneName, setCloneName] = useState('');
  const [cloned, setCloned] = useState(null);
  const rows = useMemo(() => docs || [], [docs]);
  const current = rows[0];

  useEffect(() => { setOpen(null); }, [pipelineId]);

  const diffOf = useMemo(() => {
    if (!open) return null;
    const rev = rows.find((r) => r.id === open);
    if (!rev) return null;
    return { rev, against: current && current.id !== rev.id ? current : null, rows: designDiff(rev.before, rev.after), vsCurrent: current && current.id !== rev.id ? designDiff(rev.after, current.after) : [] };
  }, [open, rows, current]);

  const rollBack = async (rev) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Roll back to v${rev.version} (${when(rev.createdAt)})? The current design is kept as v${(current?.version || 0)} and the roll-back becomes v${(current?.version || 0) + 1}.`)) return;
    setBusy(rev.id); setNote('');
    try {
      const r = await api.post(`/revisions/${rev.id}/restore`, {});
      setNote(`Rolled back — now v${r.revision.version}. Reload the canvas to see it.`);
      onRestored?.(r.revision);
    } catch (e) { onError?.(e.message); setNote(e.message); }
    setBusy('');
  };
  const snapshotNow = async () => {
    setBusy('snap'); setNote('');
    try {
      const r = await api.post('/revisions', { reason: 'manual checkpoint' });
      setNote(r.revision ? `Checkpoint v${r.revision.version} recorded.` : 'Nothing changed since the last revision.');
    } catch (e) { setNote(e.message); }
    setBusy('');
  };
  const clone = async () => {
    setBusy('clone'); setNote(''); setCloned(null);
    try {
      const r = await api.post('/clone', cloneName.trim() ? { name: cloneName.trim() } : {});
      setCloned(r);
    } catch (e) { setNote(e.message); }
    setBusy('');
  };

  return (
    <div data-nova-note={`History card: ${rows.length} design revision(s)${current ? `, current v${current.version} from ${when(current.createdAt)}` : ''}. Each row diffs against the design before it; Roll back restores that design as a new revision. Clone makes a deep copy (graph, tests, requirements, budget, capabilities; schedules off).`}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="cs-help" style={{ fontSize: 11.5, ...mono }} title="pipeline id">{pipelineId}</span>
        <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => copyText(pipelineId)} title="Copy the pipeline id">Copy id</button>
        <span style={{ flex: 1 }} />
        <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={!!busy} onClick={snapshotNow} title="Record the current design as a revision now (autosaves coalesce after ~45 s of quiet)">Checkpoint</button>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
        <input className="cs-input" style={{ flex: 1, padding: '2px 6px', fontSize: 12 }} placeholder={`${String(pipeline?.name || 'pipeline').replace(/^Example:\s*/i, '')} (copy)`} value={cloneName} onChange={(e) => setCloneName(e.target.value)} aria-label="clone name" />
        <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={!!busy} onClick={clone} title="Deep copy: blocks (new ids), wires, tests, requirements, budget, kind, capabilities. Schedules start off.">Clone</button>
      </div>
      {cloned && (
        <p className="cs-help" style={{ fontSize: 12, marginTop: 6 }}>
          Copied {cloned.blocks} block{cloned.blocks === 1 ? '' : 's'} → <Link to={`/orgs/${orgId}/ws/${cloned.workspaceId}/pipelines/${cloned.pipelineId}`}>open the copy</Link>
        </p>
      )}
      {note && <p className="cs-help" style={{ fontSize: 12, marginTop: 6 }}>{note}</p>}

      <ul className="nq-revisions">
        {rows.length === 0 && <li className="cs-help" style={{ fontSize: 12 }}>No revisions yet — the first is cut when the design settles (or press Checkpoint).</li>}
        {rows.map((r) => (
          <li key={r.id} className={open === r.id ? 'is-open' : ''}>
            <button type="button" className="nq-rev-row" onClick={() => setOpen(open === r.id ? null : r.id)} aria-expanded={open === r.id}>
              <span className="nq-rev-v">v{r.version}</span>
              <span className="nq-rev-what">
                {r.reason || (r.changedKeys || []).join(', ') || 'change'}
                {r.blocksChanged?.length ? ` · ${r.blocksChanged.length} block${r.blocksChanged.length === 1 ? '' : 's'}` : ''}
                {r.code && Object.keys(r.code).length ? ` · code ${Object.values(r.code).map((c) => shortSha(c.sha || c.ref)).join(', ')}` : ''}
              </span>
              <span className="nq-rev-who">{r.actorEmail || r.actor || ''} · {when(r.createdAt)}</span>
            </button>
            {open === r.id && diffOf && (
              <div className="nq-rev-diff">
                {diffOf.rows.length === 0 && <span className="cs-help" style={{ fontSize: 12 }}>No design change recorded (position-only or a checkpoint).</span>}
                {diffOf.rows.map((d, i) => (
                  <div key={i} className={`nq-rev-line is-${d.change}`}>
                    <span className="nq-rev-sign">{d.change === 'added' ? '+' : d.change === 'removed' ? '−' : '~'}</span>
                    <span style={mono}>{d.kind === 'field' ? `${d.text}: ` : `${d.kind} ${d.id ? `${d.id} ` : ''}`}</span>
                    <span className="cs-help" style={{ fontSize: 12 }}>{d.kind === 'field' ? `${JSON.stringify(d.before ?? null).slice(0, 80)} → ${JSON.stringify(d.after ?? null).slice(0, 80)}` : d.text}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
                  {diffOf.against ? (
                    <>
                      <span className="cs-help" style={{ fontSize: 11.5 }}>{diffOf.vsCurrent.length} difference{diffOf.vsCurrent.length === 1 ? '' : 's'} from the current design</span>
                      <span style={{ flex: 1 }} />
                      <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={!!busy} onClick={() => rollBack(r)} data-agent-guard="true" title="Restore this design as a new revision">↶ Roll back to v{r.version}</button>
                    </>
                  ) : <span className="cs-help" style={{ fontSize: 11.5 }}>This is the current design.</span>}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
